import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button, FormField, Input } from '@vd/ui';
import { AuthLayout } from './AuthLayout';
import { useAuth } from '../../features/auth/AuthProvider';
import { api } from '../../lib/api';

export function SignInPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { signIn, user, loading } = useAuth();
  const navigate = useNavigate();
  // `from` is the full Location object captured by RequireAuth — keeping
  // the whole shape (pathname + search + hash) is critical for the
  // extension OAuth handoff, which lives at /oauth/extension?client_id=
  // &state=&code_challenge=&redirect_uri=. Dropping the query string here
  // strands the consent page on missing params and the extension flow
  // breaks for any user not already signed in.
  const location = useLocation() as { state?: { from?: { pathname?: string; search?: string; hash?: string } } };

  // Already-signed-in visitors landing on /signin go straight to the
  // assigned inbox (or back to whatever route bounced them here, if
  // RequireAuth set a `from`). Don't render the form at all in that
  // state — the navigate fires before paint.
  if (loading) {
    return <AuthLayout title="Sign in" subtitle="Welcome back."><div /></AuthLayout>;
  }
  if (user) {
    const from = location.state?.from;
    if (from?.pathname) {
      return <Navigate to={{ pathname: from.pathname, search: from.search ?? '', hash: from.hash ?? '' }} replace />;
    }
    return <Navigate to="/inbox/assigned" replace />;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      // Sign-in destination priority:
      //   1. The page they were trying to reach before being bounced to
      //      sign-in (saved by the auth guard).
      //   2. Their last-opened project board, if they've used the system.
      //   3. Home (welcome panel) for brand-new users.
      // The home-state fetch is best-effort — on failure we fall back to /.
      const from = location.state?.from;
      if (from?.pathname) {
        navigate(
          { pathname: from.pathname, search: from.search ?? '', hash: from.hash ?? '' },
          { replace: true },
        );
      } else {
        try {
          const state = await api.getHomeState();
          if (state.hasTickets && state.lastProjectId) {
            navigate(`/projects/${state.lastProjectId}/board`, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        } catch {
          navigate('/', { replace: true });
        }
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back."
      footer={<>New here? <Link to="/signup">Create an account</Link></>}
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FormField label="Email">
          <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </FormField>
        <FormField label="Password" help={<Link to="/forgot">Forgot password?</Link>}>
          <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </FormField>
        {error && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{error}</div>}
        <Button variant="primary" size="lg" loading={submitting} type="submit">
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
